import { z } from 'zod';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { createCustomVizRequestSchema } from 'libs/end-to-end/src/custom-viz/createCustomVizRequestSchema';
import { FilterDefinitionForExtras } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { ExplanationsOutputDictionary } from 'libs/end-to-end/src/explanations';
import { ViewConfigurationSchema } from 'libs/end-to-end/src/view-configurations';

// 1. The request type
export const TableViewInputs = z.object({
  dropdowns: EmptyObject,
  extras: z.object({
    groupBy: z.string().nullable(),
    secondaryBreakdown: z.string().optional(),
    metrics: z.array(z.string()).optional(),
    // The name of this variable is not ideal, but it really means "selected rows to show" where the rows are identified by their index represented as a string.
    visibleDepartments: z.array(z.string()).optional(),
    filters: FilterDefinitionForExtras.array(),
  }),
});

export const TableViewRequest = createCustomVizRequestSchema(
  'tableView',
  TableViewInputs,
);

export type TableViewRequest = z.infer<typeof TableViewRequest>;

export const TableViewSecondaryBreakdownRow = z
  .object({
    primaryGroupValue: z.string(),
    secondaryGroupValue: z.string(),
    metrics: z.array(z.string()),
  })
  .describe('TableViewSecondaryBreakdownRow');

export type TableViewSecondaryBreakdownRow = z.infer<
  typeof TableViewSecondaryBreakdownRow
>;

export const TableViewSecondaryBreakdownData = z
  .object({
    breakdownColumn: z.string(),
    breakdownValues: z.array(z.string()),
    rows: z.array(TableViewSecondaryBreakdownRow),
    sampleSizes: z.array(z.array(z.number())),
    confidenceValues: z.array(z.array(z.string())),
    impactValues: z.array(z.array(z.string())),
    fullSampleAverage: z.array(z.string()),
  })
  .describe('TableViewSecondaryBreakdownData');

export type TableViewSecondaryBreakdownData = z.infer<
  typeof TableViewSecondaryBreakdownData
>;

// 2. The result type
export const TableViewOutput = z
  .object({
    // Primary breakdown data (current structure)
    primaryColumnHeaders: z.array(z.string()),
    primaryRows: z.array(z.array(z.string())),

    // Secondary breakdown data (when secondaryBreakdown is provided)
    secondaryBreakdownData: TableViewSecondaryBreakdownData.optional(),

    // Supporting data
    sampleSizes: z.array(z.array(z.number())),
    numeratorSizes: z.array(z.array(z.number())),
    booleanColumns: z.array(z.string()),
    confidenceValues: z.array(z.array(z.string())),
    impactValues: z.array(z.array(z.string())),
    fullSampleAverage: z.array(z.string()),
  })
  .describe('TableViewOutput');

export type TableViewOutput = z.infer<typeof TableViewOutput>;

export const TableViewComponentProps = z.object({
  data: TableViewOutput,
  selectedGroupBy: z.string().nullable(),
  selectedSecondaryBreakdown: z.string().optional(),
  isLoading: z.boolean(),
  viewConfiguration: ViewConfigurationSchema,
});

export type TableViewComponentProps = z.infer<typeof TableViewComponentProps>;

export const getGroupByFromTableViewRequest = ({
  request,
}: {
  request: TableViewRequest;
}): string | null => {
  return request.inputs.extras.groupBy;
};

const TableViewCompatibleData = z
  .object({
    columnHeaders: z.string().array(),
    rows: z.string().array().array(),
    rowsMeta: z
      .object({
        groupByLevel: z.string(),
        secondaryBreakdownLevel: z.string().optional(),
      })
      .array(),
    sampleSizes: z.number().array().array(),
    booleanColumns: z.string().array(),
    confidenceValues: z.string().array().array(),
    impactValues: z.string().array().array(),
    fullSampleAverage: z.string().array(),
    selectedGroupBy: z.string().nullable().optional(),
    explanations: ExplanationsOutputDictionary.optional(),
    filtersToBreakdown: z
      .object({
        label: z.string(),
        value: z.string(),
      })
      .array()
      .optional(),
    numeratorSizes: z.number().array().array(),
  })
  .describe('TableViewCompatibleData');

export type TableViewCompatibleData = z.infer<typeof TableViewCompatibleData>;

const TableViewModeArguments = z.object({
  data: TableViewCompatibleData,
  visibleRows: z.number().array(),
  viewConfiguration: ViewConfigurationSchema,
});

export type TableViewModeArguments = z.infer<typeof TableViewModeArguments>;

const TableViewFilterProps = z.object({
  data: TableViewCompatibleData,
  viewConfiguration: ViewConfigurationSchema,
  setViewConfiguration: z.function().args(ViewConfigurationSchema).optional(),
});

export type TableViewFilterProps = z.infer<typeof TableViewFilterProps>;

export const getSecondaryBreakdownFromTableViewRequest = ({
  request,
}: {
  request: TableViewRequest;
}): string | undefined => {
  return request.inputs.extras.secondaryBreakdown || undefined;
};

export const HeatmapViewModeArguments = z.object({
  data: TableViewCompatibleData,
  viewConfiguration: ViewConfigurationSchema,
});

export type HeatmapViewModeArguments = z.infer<typeof HeatmapViewModeArguments>;
